import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  LargeViewOnly,
  StickyButtons,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import BluettiCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import Accordion from "../../../components/accordion";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import Tags from "../../../components/tags";
import YouTube from "../../../components/youtube";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

let headerInfo = lookupByUrl(namedRoutes.bluetti.rv5);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionX={40}
      positionY={66}
      background="/images/products/bluetti/rv5/bluetti-rv5-system.webp"
      titlePrefix="BLUETTI"
      title="RV5 Power System"
      subTitle="Vanlife Review"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <LargeViewOnly>
          <ButtonList size="medium">
            <Button
              size="medium"
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/rv5-rv-solar-system"
              icon="Bluetti"
            >
              View on BLUETTI
            </Button>
            <Button
              size="medium"
              affiliate="https://amzn.to/4aC6qjL"
              href="https://www.amazon.com/dp/B0G13DFFL5/"
              secondary
              icon="Amazon"
            >
              View on Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>

        <p>
          The Bluetti RV5 enters the modular RV power market as a cost-effective
          challenger to established kits. It delivers a robust 48V battery
          architecture and 5,000W continuous AC output, with installation
          simplified by the fact that all required cabling is included.
        </p>
        <p>
          While it positions itself as a competitor to the{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/power-kits-collection"
          >
            EcoFlow Power Kit
          </Link>{" "}
          or DIY Victron or Renogy builds, potential buyers need to look closely
          at the specs. It is a powerful system, but it is held back by some
          baffling design limitations that may disqualify it for specific
          off-grid setups.
        </p>

        <AffiliateDisclosure />

        <ImageSet
          size={5}
          border={true}
          showAlt
          images={[
            {
              lg: "/images/products/bluetti/rv5/rv5_b4810_epanel_epad.webp",
              alt: "RV5 power system",
            },
            {
              lg: "/images/products/bluetti/rv5/rv5-front.webp",
              alt: "RV5 Power Hub",
            },
            {
              lg: "/images/products/bluetti/rv5/b4810-back-side.webp",
              alt: "B4810 LiFePO₄ Battery 5120Wh",
            },
            {
              lg: "/images/products/bluetti/rv5/epanel-front.webp",
              alt: "Epanel Smart Distribution Panel",
            },
            {
              lg: "/images/products/bluetti/rv5/epad-front.webp",
              alt: "Epad Control Screen",
            },
          ]}
        />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Highlights</SlugLink>
                  </li>
                  <li>
                    <SlugLink>RV5: Power Hub</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Epanel: Smart Distribution Panel</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Epad: Control Screen</SlugLink>
                  </li>
                  <li>
                    <SlugLink>B4810: LiFePO₄ Battery</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Solar Panels</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Edock: Battery Expansion</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Install Video</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Limitations & Suggestions</SlugLink>
                  </li>
                  <li>
                    <SlugLink>My Verdict</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>

      <Center>
        <SlugHeading tag="h2">Highlights</SlugHeading>
        <ul>
          <li>
            <strong>Core Component:</strong> RV5 Power Hub (inverter, charger,
            dual MPPT)
          </li>
          <li>
            <strong>Optional Components:</strong>
            <ul>
              <li>Epanel Smart Distribution Panel</li>
              <li>Epad Control Screen</li>
              <li>B4810 LiFePO₄ Battery (5.12kWh)</li>
            </ul>
          </li>
          <li>
            <strong>Adaptable:</strong> Supports 3rd party components
          </li>
          <li>
            <strong>AC Output:</strong> 5,000W
          </li>
          <li>
            <strong>DC Output:</strong> 100A (12V), 60A (24V)
          </li>
          <li>
            <strong>4 Charging Methods:</strong> solar, alternator, shore power,
            EV station
          </li>
          <li>
            <strong>Warranty: </strong> 5-years
          </li>
          <li>
            <strong>Price Range:</strong> Highly dependent on configuration
            <ul>
              <li>
                <Link
                  affiliateData={affiliateMerchants.bluetti}
                  href="https://www.bluettipower.com/products/rv5-rv-solar-system?id=68da543e4197d774e9a802da&variant=47636897038555"
                >
                  Hub
                </Link>
                : $1,499
              </li>
              <li>
                <Link
                  affiliateData={affiliateMerchants.bluetti}
                  href="https://www.bluettipower.com/products/rv5-rv-solar-system?id=68da7c684197d774e9a80362&variant=47637171503323"
                >
                  Hub+Battery+Panel+Screen
                </Link>
                : $3,999
              </li>
              <li>
                <Link
                  affiliateData={affiliateMerchants.bluetti}
                  href="https://www.bluettipower.com/products/rv5-rv-solar-system?id=68da82e64197d774e9a80366&variant=47637191393499"
                >
                  Hub+Battery(x2)+Panel+Screen
                </Link>
                : $5,999
              </li>
            </ul>
          </li>
        </ul>

        <ImageSet
          size={1}
          images={[
            {
              lg: "/images/diagrams/power-systems/bluetti/bluetti-rv5.webp?v=2",
              alt: "BLUETTI RV5 vanlife system diagram",
            },
          ]}
        />
      </Center>

      <Center>
        <Image
          right
          border
          src="/images/products/bluetti/rv5/rv5-front-right.webp"
          alt=""
          showAlt
        />
        <SlugHeading tag="h2">RV5: Power Hub</SlugHeading>
        <p style="margin-top:-1em;">
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/rv5-rv-solar-system?id=68da543e4197d774e9a802da&variant=47636897038555"
          >
            Details
          </Link>{" "}
          |{" "}
          <Link
            external
            href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/09/RV5%C2%A0User%20Manual%20US%20EN-1759160161231-e9c8.pdf"
          >
            Manual
          </Link>{" "}
          |{" "}
          <Link
            external
            href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/09/RV5%20Quick%20Start%20Guide-1759160142458-4541.pdf"
          >
            Setup Guide
          </Link>
        </p>
        <p>
          The Bluetti RV5 Power Hub delivers 5,000W of continuous AC output in a
          rugged, UL-certified, IP65-rated chassis designed for advanced users.
        </p>

        <ul>
          <li>
            <strong>Price:</strong> $1,499
          </li>
          <li>
            <strong>System Battery Voltage:</strong> 48V
          </li>
        </ul>

        <h3>Solar Inputs</h3>
        <p>
          The solar input’s low 50V maximum forces panels to be wired in
          parallel and can quickly exceed what standard MC4 connectors are rated
          for. PV2 must be dedicated to either solar or alternator charging, but
          not both (PV = photovoltaic).
        </p>
        <ul>
          <li>
            <strong>Ports PV1/PV2:</strong> <NoWrap>12-50V⎓50A (1,800W)</NoWrap>{" "}
            each
          </li>
        </ul>

        <h3>Alternator Charging</h3>
        <p>
          While compatible with both 12V and 24V alternators, performance on
          standard 12V systems is underwhelming. Capped at 600W, it lags behind
          competitors delivering 1,000W+. At this rate, recharging the 5kWh
          battery from empty requires a grueling 8.5 hours of driving.
        </p>
        <ul>
          <li>
            <strong>Port PV2:</strong> <NoWrap>12-14.4V⎓50A (600W)</NoWrap> /{" "}
            <NoWrap>24-28.8V⎓50A (1,200W)</NoWrap>
          </li>
        </ul>

        <h3>AC Inputs</h3>
        <p>
          With dual AC input modes, the RV5 can recharge quickly from standard
          shore power or 208V EV hookups.
        </p>
        <ul>
          <li>
            <strong>Standard:</strong> 120V~42A
          </li>
          <li>
            <strong>EV:</strong> 208V/240V~22A
          </li>
        </ul>

        <h3>AC Output</h3>
        <p>
          The RV5 delivers 5,000W (120V~42A) AC output, providing
          household‑grade power for appliances and electronics.
        </p>

        <h3>DC Output</h3>
        <p>
          With up to 100A DC output, the RV5 delivers more current than most
          power stations, enough to power all your DC needs.
        </p>
        <ul>
          <li>12V⎓100A (1,360W)</li>
          <li>24V⎓60A (1,620W)</li>
        </ul>

        <Accordion
          items={[
            {
              heading: "More Details",
              content: (
                <Fragment>
                  <ul>
                    <li>
                      <strong>Max Battery Input/Output Current:</strong> 150A
                    </li>
                    <li>
                      <strong>AC Bypass:</strong> 50A Max (EV does not support
                      bypass mode)
                    </li>
                    <li>
                      <strong>Uninterruptible Power Supply (UPS):</strong> ≤20ms
                      switchover time
                    </li>
                    <li>
                      <strong>Weight:</strong> 30.86lbs (14kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 17.72 × 19.69 × 6.30in (450 ×
                      500 × 159.9mm)
                    </li>
                    <li>
                      <strong>Operating Temperature:</strong> -4°F to 113°F
                      (-20°C to 45°C) | Derated performance up to 131°F (55°C).
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -4°F to 131°F (-20°C
                      to 55°C)
                    </li>
                    <li>
                      <strong>Working altitude:</strong> ≤2000m
                    </li>
                    <li>
                      <strong>Operating Humidity:</strong> 95% Max
                    </li>
                    <li>
                      <strong>Certification:</strong> UL458
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <Image
          right
          border
          src="/images/products/bluetti/rv5/epanel-front.webp"
          alt=""
          showAlt
        />
        <SlugHeading tag="h2">Epanel: Smart Distribution Panel</SlugHeading>
        <p style="margin-top:-1em;">
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/epanel?_pos=2&_sid=ed510a344&_ss=r&variant=47637134442715"
          >
            Details
          </Link>{" "}
          |{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/09/Epanel%C2%A0User%20Manual%20US%20EN-FR-1759160492873-3d8c.pdf"
          >
            Manual
          </Link>
        </p>
        <p>
          The Epanel distributes AC and DC power across the system and includes
          smart circuits controllable from the control screen or app.
        </p>

        <ul>
          <li>
            <strong>Price:</strong> $499
          </li>
        </ul>

        <h3>AC Circuits</h3>
        <ul>
          <li>
            <strong>AC Input:</strong> 120V/240V, 50A Max (two ports)
          </li>
          <li>
            <strong>AC Output:</strong> 120V, 30A Max per circuit (4 standard
            outlets, 4 smart outlets)
          </li>
        </ul>

        <h3>DC Circuits</h3>
        <ul>
          <li>
            <strong>Input:</strong> 10V to 30V, 100A Max
          </li>
          <li>
            <strong>Output:</strong> 10V to 30V, 20A Max per circuit (6 smart
            circuits, 14 standard circuits)
          </li>
          <li>
            <strong>Fuse Type:</strong> Blade
          </li>
        </ul>

        <p>
          The Epanel is not a turnkey solution right out of the box. You must
          still handle the "last mile" of the installation, which includes
          sourcing and installing your own AC breakers to match your specific
          circuits. Additionally, you are responsible for running AC and DC
          cabling to appliances, sizing wires correctly to prevent voltage drop,
          and installing all outlets and switches throughout the vehicle.
        </p>

        <Accordion
          items={[
            {
              heading: "More Details",
              content: (
                <Fragment>
                  <ul>
                    <li>
                      <strong>Weight:</strong> 7.7 lbs (3.5 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 17 × 13.89 × 4.12 in (431.8 ×
                      352.9 × 104.6 mm)
                    </li>
                    <li>
                      <strong>Operating Temperature:</strong> -4°F to 113°F
                      (-20°C to 45°C)
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -4°F to 158°F (-20°C
                      to 70°C)
                    </li>
                    <li>
                      <strong>Max. Operating Humidity:</strong> 90%
                    </li>
                    <li>
                      <strong>IP Rating:</strong> IP20
                    </li>
                    <li>
                      <strong>Operating Altitude:</strong> ≤6,561.68 ft (2,000
                      m)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <Image
          right
          border
          src="/images/products/bluetti/rv5/epad-front.webp"
          alt=""
          showAlt
        />
        <SlugHeading tag="h2">Epad: Control Screen</SlugHeading>
        <p style="margin-top:-1em;">
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/epad?_pos=1&_sid=98d7ec1dc&_ss=r&variant=47637148270811"
          >
            Details
          </Link>{" "}
          |{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/09/Epad%20User%20Manual%20EN-FR-DE-1759160214843-d858.pdf"
          >
            Manual
          </Link>
        </p>
        <p>
          Beyond serving as a control display, the Epad includes sensor inputs
          for temperature probes and tank level sensors.
        </p>
        <ul>
          <li>
            <strong>Price:</strong> $399
          </li>
          <li>
            <strong>Display:</strong> 10.1" color LCD touchscreen
          </li>
          <li>
            <strong>Analog Inputs:</strong> 3x for resistive level sensors / 3x
            for NTC temperature sensor probe
          </li>
          <li>
            <strong>Digital Inputs:</strong> 2x
          </li>
          <li>
            <strong>Dry Contact Outputs:</strong> 2x
          </li>
        </ul>
        <Accordion
          items={[
            {
              heading: "More Details",
              content: (
                <Fragment>
                  <ul>
                    <li>
                      <strong>Resolution:</strong> 1280 x 800
                    </li>
                    <li>
                      <strong>Power Consumption:</strong> 6W (screen on) / 2.5W
                      (screen off)
                    </li>
                    <li>
                      <strong>microSD Slot :</strong> Supports SDHC cards (32 GB
                      Max.)
                    </li>
                    <li>
                      <strong>USB-A × 2:</strong> 5 V/1.5 A total, supports up
                      to 32 GB USB flash drives per port
                    </li>
                    <li>
                      <strong>Onboard Temperature Measurement:</strong> -20°C to
                      70°C (±2°C)/-4°F to 158°F (±3.6°F)
                    </li>
                    <li>
                      <strong>Onboard Humidity Measurement:</strong> 0% to 100%
                      (±2%)
                    </li>
                    <li>
                      <strong>Temperature Sensor (included):</strong> -20°C to
                      70°C (±2°C)/-4°F to 158°F (±3.6°F)
                    </li>
                    <li>
                      <strong>Buzzer:</strong> Built-in
                    </li>
                    <li>
                      <strong>Ambient Light Sensor:</strong> Built-in for auto
                      screen brightness adjustment
                    </li>
                    <li>
                      <strong>Weight:</strong> 1.48 lbs (0.67 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 9.72 × 6.22 × 1.35 in (247 ×
                      158 × 34.2 mm)
                    </li>
                    <li>
                      <strong>Operating Temperature:</strong> -10°C to 55°C/14°F
                      to 131°F
                    </li>
                    <li>
                      <strong>Storage Temperature:</strong> -20°C to 55°C/-4°F
                      to 131°F
                    </li>
                    <li>
                      <strong>Protection Rating:</strong> IP20
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <Image
          right
          border
          src="/images/products/bluetti/rv5/b4810-front-side.webp"
          alt=""
          showAlt
        />
        <SlugHeading tag="h2">B4810: LiFePO₄ Battery</SlugHeading>
        <p style="margin-top:-1em;">
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-b4810-lifepo-battery-51-2-v-100-ah?id=68ce222928930efe7edb031e&variant=47618664857819"
          >
            Details
          </Link>{" "}
          |{" "}
          <Link
            external
            href="https://cdn.shopify.com/s/files/1/0558/1199/7864/files/B4810_User_Manual_US_EN-FR-ES.pdf?v=1758987099"
          >
            Manual
          </Link>
        </p>
        <p>
          The B4810 battery is built for all‑season reliability, with
          self‑heating that keeps it performing even in freezing conditions.
        </p>
        <ul>
          <li>
            <strong>Price:</strong> $1,999
          </li>
          <li>
            <strong>Capacity:</strong> 5.12 kWh (51.2V, 100Ah)
          </li>
          <li>
            <strong>Operating Voltage:</strong> 40-58.4V
          </li>
          <li>
            <strong>Charging Current:</strong> 50 A (Recommended), 100 A Max
          </li>
          <li>
            <strong>Discharging Current:</strong> 100 A Max
          </li>
          <li>
            <strong>Peak Discharging Current:</strong> 200 A @ 30 s
          </li>
        </ul>
        <Accordion
          items={[
            {
              heading: "More Details",
              content: (
                <Fragment>
                  <ul>
                    <li>
                      <strong>Charging Temperature:</strong> -4°F to 131°F
                      (-20°C to 55°C)
                    </li>
                    <li>
                      <strong>Discharging Temperature:</strong> -4°F to 131°F
                      (-20°C to 55°C)
                    </li>
                    <li>
                      <strong>Storage Temperatures:</strong>
                      <ul>
                        <li>Up to 1 month: -4°F to 104°F (-20°C to 40°C)</li>
                        <li>Up to 6 months: -4°F to 77°F (-20°C to 25°C)</li>
                        <li>
                          Perform a full charge and discharge cycle before the
                          storage period ends.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Operating Humidity:</strong> 5% to 95% RH
                    </li>
                    <li>
                      <strong>IP Rating:</strong> IP65
                    </li>
                    <li>
                      <strong>Operating Altitude:</strong> ≤6,561.68 ft (2,000
                      m)
                    </li>
                    <li>
                      <strong>Cycle Life:</strong> 6,000 cycles while retaining
                      70% capacity @ 0.5C charge/discharge, 77°F (25°C)
                    </li>
                    <li>
                      <strong>Weight:</strong> 101.4 lbs (46 kg)
                    </li>
                    <li>
                      <strong>Dimensions:</strong> 23.94 × 14.96 × 5.71 in (608
                      × 380 × 145 mm)
                    </li>
                  </ul>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>

      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>
        <SlugHeading tag="h3">Solar Panels</SlugHeading>
        <p>
          While Bluetti provides various solar panel options, always prioritize
          panels that physically fit your vehicle; using other reputable brands
          is perfectly fine. The following panels are often bundled with the RV5
          system.
        </p>
        <PhotoListItem
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/pv100fx-flexible-solar-panel?_pos=1&_sid=ee3ea5cee&_ss=r&variant=47607050371291"
          img="/images/products/bluetti/rv5/pv100_fx_2x.webp"
          title="PV100 FX Flexible Solar Panels"
          description={<Fragment>Comes in sets of 2, 4, or 6</Fragment>}
          buttonText="BLUETTI"
          buttonIcon="Bluetti"
        />

        <SlugHeading tag="h3">Edock: Battery Expansion</SlugHeading>
        <p>
          Built for third‑party battery compatibility and large‑scale expansion,
          the Edock serves as the communication interface required for
          integrating external devices and managing parallel connections, though
          it is not yet available for purchase.
        </p>
        <p>
          The RV5 supports parallel connection of as many as 24 batteries, but
          setups larger than 8 require Edock interface boxes, each capable of
          handling communication for 8 units.
        </p>
      </Center>

      <Center>
        <SlugHeading tag="h3">Install Video</SlugHeading>
        <YouTube id="hZ_rIKBdIOM" />
      </Center>

      <Center>
        <SlugHeading tag="h2">Limitations & Suggestions</SlugHeading>
        <p>
          While the RV5 is a capable system, there are a few unexpected
          limitations worth noting if you’re considering it for your setup:
        </p>
        <ul>
          <li>
            <strong>Solar Voltage: </strong> It's baffling that Bluetti markets
            the RV5 as a high-efficiency 48V system yet restricts solar input to
            a low 50V. This effectively blocks efficient high-voltage series
            arrays and forces the user back into complex parallel wiring. As
            demonstrated by Victron, 150V MPPTs can fully meet UL1741 safety
            standards while offering far better real-world usability (see
            certifications:{" "}
            <Link
              external
              href="https://www.victronenergy.com/upload/documents/Certificate-of-Compliance%2C-UL-1741-and-CSA-C22.2%2C-all-BlueSolar-%26-SmartSolar-MPPTs-up-to-150-100.pdf"
            >
              1
            </Link>
            ,{" "}
            <Link
              external
              href="https://www.victronenergy.com/upload/documents/Certificate-UL-1741-and-CSA-C22.2,-16938-1S-SmartSolar-MPPTs-150-45-up-to-250-100.pdf"
            >
              2
            </Link>
            ).
          </li>
          <li>
            <strong>Solar Amperage: </strong> The resulting 50A input exceeds
            the standard 30A rating of most MC4 connectors. This mismatch
            creates potential safety issues and necessitates heavy-gauge cabling
            and branch connectors that negate some of the system's promised
            simplicity. While parallel wiring is often defended for better
            shading resilience, this benefit is overstated. Modern panels
            utilize bypass diodes to handle partial shading effectively without
            the need for high-amperage setups.
          </li>
          <li>
            <strong>Single Solar Input: </strong> Because PV2 is often reserved
            for alternator charging, the RV5 is effectively limited to a single
            solar array. This reduces flexibility for multi‑array setups.
          </li>
          <li>
            <strong>Altitude:</strong> The manuals list an operating altitude of
            ≤6,561 ft (2,000 m). If accurate, this could be a significant
            limitation for outdoor enthusiasts who travel or camp at higher
            elevations.
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">My Verdict</SlugHeading>
        <p>
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/rv5-rv-solar-system"
          >
            Bluetti RV5
          </Link>{" "}
          is a capable and affordable entry in the modular RV power market. It
          offers a professional‑style installation experience, solid AC and DC
          output, and a simplified setup at a lower price point than premium
          kits.{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/power-kits-collection"
          >
            EcoFlow's Power Kit
          </Link>{" "}
          is more polished overall, but it costs more and supports fewer
          third‑party components, giving RV5 an edge.
        </p>

        <p>
          If you want a system that feels professional without the premium price
          tag, the RV5 is worth considering. It is a strong contender for
          vanlife power setups, provided you are aware of its limitations and
          willing to work within them.
        </p>

        <BluettiCouponCode
          mini
          href="https://www.bluettipower.com/products/rv5-rv-solar-system"
          buttonText="Buy from BLUETTI"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.powerSystems}>power systems guide</Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/rv5-rv-solar-system"
            icon="Bluetti"
          >
            Buy from BLUETTI
          </Button>
          <Button
            affiliate="https://amzn.to/4aC6qjL"
            href="https://www.amazon.com/dp/B0G13DFFL5/"
            secondary
            icon="Amazon"
          >
            Buy on Amazon
          </Button>
        </ButtonList>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons title="BLUETTI RV5" description="Power System">
        <Button
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/rv5-rv-solar-system"
          icon="Bluetti"
        >
          View on BLUETTI
        </Button>
        <Button
          affiliate="https://amzn.to/4aC6qjL"
          href="https://www.amazon.com/dp/B0G13DFFL5/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
